import axios from 'axios';
import IResponse from '../interfaces/IResponse';
import { baseURL } from '../config';
import ApiHelper from './ApiHelper';

const BASE_URL = `${baseURL}api/time-tracking/esp32`;
const DELETE_SCAN_URL = `${baseURL}api/time-tracking/scans`;
const LIVE_URL = `${baseURL}api/time-tracking/live`;
const ISSUES_URL = `${baseURL}api/time-tracking/issues`;
const EMPLOYEE_MONTH_REPORT_URL = `${baseURL}api/time-tracking/reports/employee`;

export interface ITimeTrackerEmployee {
	employeeId?: string;
	firstName: string;
	lastName: string;
}

export interface ITimeTrackerScan {
	scanId: string;
	uid: string;
	counter: number;
	createdAt: string;
	time: string;
	place: string;
	type: string;
	activityType?: string | null;
	activityLabel?: string | null;
	actionPhase?: 'start' | 'end' | 'blocked' | null;
	duration?: string | null;
	durationMs?: number | null;
	device_id: string;
	station_id: string;
	employee: ITimeTrackerEmployee | null;
	employeeName: string | null;
}

interface IGetAllScansResponse extends IResponse {
	scans: ITimeTrackerScan[];
}

export type GetAllScansParams = {
	from?: string;
	to?: string;
	limit?: number;
	skip?: number;
};

const getAllScans = async (params?: GetAllScansParams): Promise<IGetAllScansResponse> => {
	const url = BASE_URL;

	try {
		const res = await axios.get(url, { params });

		return {
			ok: true,
			status: res.status,
			message: 'Pobrano skany',
			scans: res.data?.data || [],
		};
	} catch (error) {
		let message = 'Nie udało się pobrać skanów';
		let status = 400;

		if (axios.isAxiosError(error) && error.response) {
			if (error.response.status === 404) message = 'Nie znaleziono skanów';
			if (error.response.status === 403) message = 'Niedostępne';
			status = error.response.status;
		}

		return {
			message,
			status,
			ok: false,
			scans: [],
		};
	}
};

interface IDeleteScanResponse extends IResponse {
	scanId?: string;
}

const deleteScan = async (scanId: string): Promise<IDeleteScanResponse> => {
	const url = `${DELETE_SCAN_URL}/${scanId}`;
	try {
		const res = await axios.delete(url, { headers: ApiHelper.createHeader() });
		return {
			ok: true,
			status: res.status,
			message: res.data?.message || 'Usunięto skan',
			scanId: res.data?.scanId,
		};
	} catch (error) {
		let message = 'Nie udało się usunąć skanu';
		let status = 400;

		if (axios.isAxiosError(error) && error.response) {
			status = error.response.status;
			message = typeof error.response.data?.message === 'string' ? error.response.data.message : message;
			if (status === 403) message = message || 'Brak uprawnień';
			if (status === 404) message = message || 'Nie znaleziono skanu';
		}

		return { ok: false, status, message };
	}
};

export interface ITimeTrackingLiveEmployee {
	employeeId: string | null;
	employeeName: string | null;
	uids: string[];
	present: boolean;
	presence: {
		open: boolean;
		startedAt: string | null;
		durationMs: number;
	};
	activities: {
		przerwa: { open: boolean; startedAt: string | null; totalMs: number; currentMs: number };
		papieros: { open: boolean; startedAt: string | null; totalMs: number; currentMs: number };
		piec: { open: boolean; startedAt: string | null; totalMs: number; currentMs: number };
	};
}

interface IGetLiveResponse extends IResponse {
	dayKey: string;
	employees: ITimeTrackingLiveEmployee[];
}

const getLive = async (date?: string): Promise<IGetLiveResponse> => {
	try {
		const res = await axios.get(LIVE_URL, { params: date ? { date } : undefined });
		return {
			ok: true,
			status: res.status,
			message: 'Pobrano live',
			dayKey: res.data?.dayKey,
			employees: res.data?.employees || [],
		};
	} catch (error) {
		let message = 'Nie udało się pobrać live';
		let status = 400;
		if (axios.isAxiosError(error) && error.response) {
			status = error.response.status;
			message = typeof error.response.data?.message === 'string' ? error.response.data.message : message;
		}
		return { ok: false, status, message, dayKey: '', employees: [] };
	}
};

export interface ITimeTrackingIssue {
	issueId: string;
	uid: string;
	activityType: string;
	activityLabel: string;
	sessionStartScanId: string;
	sessionStartedAt: string;
	status: 'open' | 'resolved';
	reason: string;
	employee: ITimeTrackerEmployee | null;
	employeeName: string | null;
}

interface IGetIssuesResponse extends IResponse {
	issues: ITimeTrackingIssue[];
}

const getIssues = async (status: 'open' | 'resolved' = 'open'): Promise<IGetIssuesResponse> => {
	try {
		const res = await axios.get(ISSUES_URL, { params: { status } });
		return {
			ok: true,
			status: res.status,
			message: 'Pobrano zgłoszenia',
			issues: res.data?.data || [],
		};
	} catch (error) {
		let message = 'Nie udało się pobrać zgłoszeń';
		let statusCode = 400;
		if (axios.isAxiosError(error) && error.response) {
			statusCode = error.response.status;
			message = typeof error.response.data?.message === 'string' ? error.response.data.message : message;
		}
		return { ok: false, status: statusCode, message, issues: [] };
	}
};

interface IResolveIssueResponse extends IResponse {}

const resolveIssue = async (issueId: string, payload: { resolution: 'unknown' } | { resolution: 'manual_end'; endAt: string }): Promise<IResolveIssueResponse> => {
	try {
		const res = await axios.post(`${ISSUES_URL}/${issueId}/resolve`, payload);
		return { ok: true, status: res.status, message: res.data?.message || 'OK' };
	} catch (error) {
		let message = 'Nie udało się zamknąć zgłoszenia';
		let statusCode = 400;
		if (axios.isAxiosError(error) && error.response) {
			statusCode = error.response.status;
			message = typeof error.response.data?.message === 'string' ? error.response.data.message : message;
		}
		return { ok: false, status: statusCode, message };
	}
};

export interface IEmployeeMonthDay {
	date: string;
	arrivalAt: string | null;
	departureAt: string | null;
	presenceEvents?: Array<{ at: string; actionPhase: 'start' | 'end'; uid: string }>;
	totalsMs: { wejscie: number; przerwa: number; papieros: number; piec: number };
}

export interface IEmployeeMonthReport {
	year: number;
	month: number;
	uids: string[];
	days: Record<string, IEmployeeMonthDay>;
	stats: {
		daysPresent: number;
		lateCount: number;
		earlyLeaveCount: number;
		totalPresenceMs: number;
		totalBreakMs: number;
		totalSmokingMs: number;
		totalFurnaceMs: number;
	};
}

interface IGetEmployeeMonthReportResponse extends IResponse {
	report: IEmployeeMonthReport | null;
}

const getEmployeeMonthReport = async (employeeId: string, year: number, month: number): Promise<IGetEmployeeMonthReportResponse> => {
	try {
		const res = await axios.get(`${EMPLOYEE_MONTH_REPORT_URL}/${employeeId}/month`, { params: { year, month } });
		return {
			ok: true,
			status: res.status,
			message: 'Pobrano raport',
			report: res.data || null,
		};
	} catch (error) {
		let message = 'Nie udało się pobrać raportu';
		let statusCode = 400;
		if (axios.isAxiosError(error) && error.response) {
			statusCode = error.response.status;
			message = typeof error.response.data?.message === 'string' ? error.response.data.message : message;
		}
		return { ok: false, status: statusCode, message, report: null };
	}
};

const timeTrackerController = {
	getAllScans,
	deleteScan,
	getLive,
	getIssues,
	resolveIssue,
	getEmployeeMonthReport,
};

export default timeTrackerController;
